import { Store, TerminalService, IErrorReporter, IErrorData, ITenant, IUser, Device } from 'app-core';

import { init, withScope, captureException, captureMessage, Scope } from '@sentry/vue';

import packageJson from '../../package.json';
import { App } from 'vue';

class ErrorReporter implements IErrorReporter {

    shouldReportErrors = false;

    constructor() {
        this.shouldReportErrors = import.meta.env.VITE_ENV === 'production' && !!import.meta.env.VITE_SENTRY_VUE_DSN;
    }

    setUpErrorReporting(app: App): void {
        if (!this.shouldReportErrors) return;

        init({
            app,
            dsn: import.meta.env.VITE_SENTRY_VUE_DSN,
            release: `konty-web@${packageJson.version}`,
            environment: import.meta.env.VITE_ENV,
            ignoreErrors: [
                'Request failed with status code 401',
                'Request failed with status code 422',
                'The node to be removed is not a child of this node',
                'Failed to update a ServiceWorker for scope',
                'Error Status 404: App not found',
            ],
            beforeSend: async (event: any) => {
                const errorData = await this.getErrorData()
                event.tags = (!event.tags ? errorData : { ...event.tags, ...errorData }) as { [key: string]: any };
                event.tags.efectiveEnvironment = import.meta.env.VITE_ENV;
                return event;
            },
        });
    }

    private getLastLogLines(count: number = 20): string[] {
        const log = Store.get<string[]>('log');
        if (!log || !Array.isArray(log)) {
            return [];
        }
        return log.slice(-count);
    }

    reportError(error: any, data: any = null): void {
        if (!this.shouldReportErrors) return;

        withScope((scope: Scope) => {
            const logLines = this.getLastLogLines(20);

            const extrasData = {
                ...(data || {}),
                logLines
            };

            scope.setExtras(extrasData);
            captureException(error);
        });
    }

    reportMessage(message: string, data: any = null): void {
        if (!this.shouldReportErrors) return;

        withScope((scope: Scope) => {
            if (data) {
                scope.setExtras(data);
            }
            captureMessage(message);
        });
    }

    async getErrorData(): Promise<IErrorData> {
        const tenant = Store.get<ITenant>('tenant')
        const user = Store.get<IUser>('user')
        const device = Device.get()
        const terminalId = await TerminalService.getId()
        return {
            tenant: tenant?.email || 'none',
            user: user?.name || 'none',
            terminal: terminalId || 'none',
            appName: packageJson.name,
            electronAppRelease: device?.electron?.appVersion || null
        }
    }
}

export const errorReporter = new ErrorReporter()
export default errorReporter
